/**
 * Shared helpers for the palette's page-action hotkeys, which fire on
 * `Alt`+letter (Option+letter on macOS) so they never collide with typing in the
 * search input and stay clear of the browser-reserved `Ctrl`/`Cmd` chords.
 *
 * Matching is on `event.code` (the PHYSICAL key), NOT `event.key`: on macOS
 * Option+C reports `event.key === 'ç'`, but `event.code` stays `'KeyC'`, so a
 * physical-key match resolves the intended letter across layouts and OSes.
 */

const LETTER_RE = /^[a-z]$/;

const isApplePlatform = (): boolean =>
  typeof navigator !== 'undefined' &&
  /Mac|iPhone|iPad/.test(navigator.platform);

/**
 * True when `event` is exactly `Alt`+`key` (no Ctrl/Cmd/Shift) for a single
 * ascii letter `a`–`z`. Non-letter keys (e.g. `'/'`) always return `false` —
 * those stay plain keys, matched by their own path.
 */
export const matchesAltLetter = (
  event: Pick<
    KeyboardEvent,
    'altKey' | 'ctrlKey' | 'metaKey' | 'shiftKey' | 'code'
  >,
  key: string,
): boolean => {
  if (!LETTER_RE.test(key)) {
    return false;
  }
  return (
    event.altKey &&
    !event.ctrlKey &&
    !event.metaKey &&
    !event.shiftKey &&
    event.code === `Key${key.toUpperCase()}`
  );
};

/**
 * Platform-aware label for a page-action hotkey: `⌥C` on Apple, `Alt+C`
 * elsewhere. A non-letter key (e.g. `'/'`) is a plain key, so it is returned
 * verbatim with no modifier prefix.
 */
export const formatAltHotkey = (key: string): string => {
  if (!LETTER_RE.test(key)) {
    return key;
  }
  const upper = key.toUpperCase();
  return isApplePlatform() ? `⌥${upper}` : `Alt+${upper}`;
};
